import { useEffect, useState, useRef, useCallback, startTransition } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Share2, Bookmark, Music2, Instagram, ExternalLink } from "lucide-react";
import { api, timeAgo } from "@/lib/api";

function TikTokIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}

function ShortPlayer({ short }) {
  const provider = short?.video_provider;
  const url = short?.video_embed_url;
  const [iframeFailed, setIframeFailed] = useState(false);

  if (!url || iframeFailed) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-black relative">
        {short?.featured_image && (
          <img src={short.featured_image} alt={short?.title} className="absolute inset-0 w-full h-full object-cover opacity-90" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {short?.video_url && (
          <a
            href={short.video_url}
            target="_blank"
            rel="noreferrer"
            className="relative z-10 px-5 py-3 rounded-full bg-white text-black font-bold text-sm uppercase tracking-widest hover:opacity-90 transition flex items-center gap-2"
          >
            ▶ Ver en {provider === "tiktok" ? "TikTok" : provider === "instagram" ? "Instagram" : "fuente"}
          </a>
        )}
      </div>
    );
  }
  const commonProps = {
    title: short.title,
    src: url,
    className: "w-full h-full",
    allowFullScreen: true,
    loading: "lazy",
    style: { border: 0 },
    onError: () => setIframeFailed(true),
  };
  if (provider === "youtube") {
    return <iframe {...commonProps} allow="autoplay; encrypted-media; picture-in-picture" />;
  }
  if (provider === "cloudflare_stream") {
    return (
      <iframe
        {...commonProps}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
      />
    );
  }
  if (provider === "mp4" || provider === "cloudinary") {
    return (
      <video
        src={url}
        className="w-full h-full object-cover"
        autoPlay
        playsInline
        loop
        muted
        controls
        poster={short.featured_image || undefined}
        onError={() => setIframeFailed(true)}
      />
    );
  }
  return <iframe {...commonProps} allow="autoplay; encrypted-media; fullscreen; picture-in-picture" />;
}

export default function ShortViewer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [shorts, setShorts] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const touchStartY = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get("/articles", { params: { is_short: true, limit: 20 } }).then((r) => {
      const list = r.data.items || [];
      const idx = Math.max(0, list.findIndex((s) => s.slug === slug));
      startTransition(() => {
        setShorts(list);
        setIndex(idx === -1 ? 0 : idx);
        setLoading(false);
      });
    });
  }, [slug]);

  const current = shorts[index];

  const goTo = useCallback((newIdx) => {
    if (newIdx < 0 || newIdx >= shorts.length) return;
    setIndex(newIdx);
    if (shorts[newIdx]) {
      window.history.replaceState(null, "", `/shorts/${shorts[newIdx].slug}`);
    }
  }, [shorts]);

  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);
  const close = () => navigate(-1);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowDown" || e.key === "ArrowRight") next();
      else if (e.key === "ArrowUp" || e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev]);

  const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dy) > 60) {
      if (dy < 0) next();
      else prev();
    }
    touchStartY.current = null;
  };

  if (loading || !current) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <p className="text-white/60 text-sm">Cargando...</p>
      </div>
    );
  }

  const share = () => {
    const url = `${window.location.origin}/shorts/${current.slug}`;
    if (navigator.share) {
      navigator.share({ title: current.title, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex items-stretch justify-center select-none"
      data-testid="short-viewer"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Backdrop image (blurred bg) */}
      {current.featured_image && (
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${current.featured_image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(40px) saturate(140%)",
          }}
        />
      )}

      {/* Progress bars (story style) */}
      <div className="absolute top-0 left-0 right-0 z-30 flex gap-1 px-3 pt-3">
        {shorts.map((s, i) => (
          <div
            key={s.id || s.slug || `short-${i}`}
            className="h-1 flex-1 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.25)" }}
          >
            <div
              className="h-full"
              style={{
                width: i < index ? "100%" : i === index ? "60%" : "0%",
                background: i === index ? "var(--accent)" : "white",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        ))}
      </div>

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 px-4 pt-7 pb-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="short-viewer-home">
          <span className="popu-logo text-base text-white">POPU<span className="dot-news accent-text">.news</span></span>
        </Link>
        <button
          onClick={close}
          className="w-9 h-9 rounded-full bg-black/40 backdrop-blur flex items-center justify-center hover:bg-black/60 transition"
          aria-label="Cerrar"
          data-testid="short-viewer-close"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* Player */}
      <div className="relative z-10 m-auto h-full max-h-screen flex items-center justify-center w-full px-3 py-14 md:px-10">
        <div
          className="relative w-full max-w-[420px] aspect-[9/16] bg-black rounded-md overflow-hidden shadow-2xl"
          style={{ maxHeight: "calc(100vh - 100px)" }}
        >
          <ShortPlayer short={current} />

          {/* Tap zones */}
          <button
            onClick={prev}
            className="absolute left-0 top-0 bottom-0 w-1/3 z-20"
            aria-label="Anterior"
            data-testid="short-viewer-prev-tap"
            style={{ background: "transparent" }}
          />
          <button
            onClick={next}
            className="absolute right-0 top-0 bottom-0 w-1/3 z-20"
            aria-label="Siguiente"
            data-testid="short-viewer-next-tap"
            style={{ background: "transparent" }}
          />

          {/* Bottom info */}
          <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
            <div className="bg-gradient-to-t from-black/90 via-black/40 to-transparent absolute inset-0 -z-10" />
            <div className="flex items-center gap-2 mb-2 overline" style={{ color: "#FFB3D1" }}>
              <span>POPU SHORT</span>
              <span className="text-white/40">·</span>
              <span className="text-white/70">{timeAgo(current.published_at)}</span>
              {current.video_provider === "tiktok" && (
                <span className="ml-1 flex items-center gap-1 text-white/80"><TikTokIcon size={12} /> TikTok</span>
              )}
              {current.video_provider === "instagram" && (
                <span className="ml-1 flex items-center gap-1 text-white/80"><Instagram size={12} /> Reel</span>
              )}
            </div>
            <p className="text-white font-headline text-lg leading-tight uppercase line-clamp-3">
              {current.title}
            </p>
            {current.bajada && (
              <p className="text-white/80 text-sm mt-1 line-clamp-2">{current.bajada}</p>
            )}
          </div>
        </div>
      </div>

      {/* Side actions (desktop only) */}
      <div className="hidden md:flex absolute right-6 bottom-1/3 flex-col gap-4 z-30">
        <button onClick={share} className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition" data-testid="short-viewer-share">
          <Share2 size={18} />
        </button>
        <button className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition" data-testid="short-viewer-bookmark">
          <Bookmark size={18} />
        </button>
        {current.video_url && (
          <a href={current.video_url} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition" data-testid="short-viewer-external">
            <ExternalLink size={18} />
          </a>
        )}
      </div>

      {/* Prev/Next arrows (desktop) */}
      {index > 0 && (
        <button
          onClick={prev}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur items-center justify-center text-white"
          data-testid="short-viewer-prev"
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {index < shorts.length - 1 && (
        <button
          onClick={next}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur items-center justify-center text-white"
          data-testid="short-viewer-next"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Mobile bottom action bar */}
      <div className="md:hidden absolute bottom-3 left-0 right-0 z-30 flex items-center justify-center gap-6 px-4">
        <button onClick={share} className="text-white/90 flex items-center gap-1.5 text-xs uppercase tracking-widest" data-testid="short-viewer-share-mobile">
          <Share2 size={16} /> Compartir
        </button>
        {current.video_url && (
          <a href={current.video_url} target="_blank" rel="noreferrer" className="text-white/90 flex items-center gap-1.5 text-xs uppercase tracking-widest">
            <ExternalLink size={16} /> Ver fuente
          </a>
        )}
        <button className="text-white/90 flex items-center gap-1.5 text-xs uppercase tracking-widest">
          <Bookmark size={16} /> Guardar
        </button>
      </div>
    </div>
  );
}
